import { Trans } from "@web-monorepo/i18n/macro";
import { useAfterUnmount, useSideEffectOnMount, useWatch } from "@web-monorepo/hooks";
import { useCallback } from "react";
import { displayClose, displayOpen } from "src/pods/realtime";
import CastControl from "src/pods/realtime/components/CastControl";
import { navigateToConundrumStep, navigateToConundrumsList, navigateToLaunchpad } from "src/pods/conundrums/navigation";
import { dispatchToolkitReduxAction } from "src/state/toolkitReduxStore";
import logEvent from "src/utils/logEvent";
import activities from "./activities";
import type { DisplaySlideProps, RemoteSlideProps, Slide } from "./types";

type ConundrumRemoteRendererProps = {
  displayOpen: (path: string) => void;
  displayClose: VoidFunction;
  params: {
    mountPoint: string;
    classId: string;
    conundrumName: string;
    stepName: string;
  };
};

function getSlide(
  role: "remote",
  conundrumName: string,
  index: number,
): React.ComponentType<RemoteSlideProps> | undefined;
function getSlide(
  role: "display",
  conundrumName: string,
  index: number,
): React.ComponentType<DisplaySlideProps> | undefined;
function getSlide(role: keyof Slide, conundrumName: string, index: number): Slide[keyof Slide] | undefined {
  const definition = activities[conundrumName];
  return definition?.[index]?.[role];
}

function ConundrumRemoteRenderer({ displayOpen, displayClose, params }: ConundrumRemoteRendererProps) {
  const { conundrumName, stepName, classId, mountPoint } = params;
  const index = parseInt(stepName, 10);

  const showDisplaySlide = useCallback(() => {
    displayOpen(`/apps/display/conundrums/${conundrumName}/${stepName}`);
  }, [displayOpen, conundrumName, stepName]);

  // Initial mount: kick off the display slide if one exists for this
  // step, and emit the entry event. (Pre-migration: componentDidMount.)
  useSideEffectOnMount(() => {
    if (getSlide("display", conundrumName, index)) {
      showDisplaySlide();
    }
    logEvent({ eventName: `conundrums.${conundrumName}.${stepName}` });
  });

  // Unmount cleanup: close the display session. (Pre-migration:
  // componentWillUnmount.) `useAfterUnmount` is the dedicated primitive
  // for this — defers via setTimeout(0) so the callback only fires on a
  // real unmount, not on a StrictMode double-effect.
  useAfterUnmount(displayClose);

  // Mid-mount param changes: when conundrum/step changes (browser
  // history nav, in-app slide nav), notify the display side and log
  // the new step. Replaces the pre-migration
  // UNSAFE_componentWillReceiveProps. `useWatch` gives both prev + next
  // values so we can compare the display-slide identity and avoid
  // calling displayOpen/displayClose if the same slide is still mounted.
  // `immediate: false` so useSideEffectOnMount above owns the initial
  // mount work without double-firing.
  useWatch(
    [conundrumName, stepName],
    ([prevConundrum, prevStep]) => {
      const prevDisplay = getSlide("display", prevConundrum, parseInt(prevStep, 10));
      const nextDisplay = getSlide("display", conundrumName, index);
      if (nextDisplay !== prevDisplay) {
        if (nextDisplay) {
          displayOpen(`/apps/display/conundrums/${conundrumName}/${stepName}`);
        } else {
          displayClose();
        }
      }
      logEvent({ eventName: `conundrums.${conundrumName}.${stepName}` });
    },
    { immediate: false },
  );

  const onIndexChange = (nextIndex: number) => {
    if (nextIndex < 0) {
      navigateToConundrumsList(mountPoint, classId);
      return;
    }
    if (!getSlide("remote", conundrumName, nextIndex)) {
      navigateToConundrumStep(mountPoint, classId, conundrumName, 0);
      return;
    }
    navigateToConundrumStep(mountPoint, classId, conundrumName, nextIndex);
  };

  const Slide = getSlide("remote", conundrumName, index);
  if (!Slide) {
    return (
      <div>
        <Trans>
          Couldn't find slide for {{ conundrumName }} {{ stepName }} (remote)
        </Trans>
      </div>
    );
  }

  const hasDisplay = Boolean(getSlide("display", conundrumName, index));

  return (
    <div>
      {hasDisplay && <CastControl onRequestStart={showDisplaySlide} />}
      <Slide
        goToLaunchpad={() => navigateToLaunchpad(mountPoint, classId)}
        goToTeacherTips={() => {
          /* this has been broken for a while now */
        }}
        next={(numSteps = 1) => onIndexChange(index + numSteps)}
        prev={(numSteps = 1) => onIndexChange(index - numSteps)}
      />
    </div>
  );
}

export default function ConundrumRemoteRendererContainer(
  props: Omit<ConundrumRemoteRendererProps, "displayOpen" | "displayClose">,
) {
  return (
    <ConundrumRemoteRenderer
      {...props}
      displayOpen={(path: string) => dispatchToolkitReduxAction(displayOpen(path))}
      displayClose={() => dispatchToolkitReduxAction(displayClose())}
    />
  );
}
